import {
  RedirectToSignIn,
  SignIn,
  useAuth,
  useClerk,
  useSession,
  useUser,
} from '@clerk/clerk-react';
import ErrorBoundary from '@src/components/common/ErrorBoundary';
import FullScreenLoadingOverlay from '@src/components/common/FullScreenLoadingOverlay';
import { CreatorStudioLanguageGate } from '@src/components/CreatorStudioLanguageGate';
import UnsupportedBrowserModal from '@src/components/UnsupportedBrowserModal';
import { ShortcutProvider } from '@src/hooks/shortcuts/ShortcutProvider';
import { useAnalyticsIdentification } from '@src/hooks/useAnalyticsIdentification';
import { useAudioSidecarFlagSync } from '@src/hooks/useAudioSidecarFlagSync';
import { useAudioThenVideoFlagSync } from '@src/hooks/useAudioThenVideoFlagSync';
import { useAuthInitialization } from '@src/hooks/useAuthInitialization';
import { useLaunchDarklyInit } from '@src/hooks/useLaunchDarklyInit';
import { useLoggingSetup } from '@src/hooks/useLoggingSetup';
import { usePylonChatBubble } from '@src/hooks/usePylonChatBubble';
import { SidebarProvider } from '@src/hooks/useSidebarState';
import { useStoreInitialization } from '@src/hooks/useStoreInitialization';
import { useTicketSignIn } from '@src/hooks/useTicketSignIn';
import { UpdateAvailabilityProvider } from '@src/hooks/useUpdateAvailability';
import { DatoContextService } from '@src/services/DatoContextService';
import { inDebugMode } from '@src/utility/debug';
import { LogLevel, TimestampFormat } from '@src/utility/Logger';
import { useLDClient } from 'launchdarkly-react-client-sdk';
import { observer } from 'mobx-react-lite';
import React from 'react';
import {
  isChrome,
  isChromium,
  isEdge,
  isEdgeChromium,
  isMobileSafari,
} from 'react-device-detect';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import styled from 'styled-components';

import BrandKit from '../components/BrandKit';
import BRollUpload from '../components/BRollUpload';
import { ReviewerRouteGuard } from '../components/common/ReviewerRouteGuard';
import ContentStudio from '../components/ContentStudio';
import Dashboard from '../components/Dashboard/';
import { DefaultLandingRoute, HomePage } from '../components/Home';
import Settings from '../components/Settings';
import SpeakerManager from '../components/SpeakerManager';
import { VideoCreator } from '../components/VideoCreator';
import RootLayout from './RootLayout';

const EditorEntry = observer(() => {
  // Clerk hooks
  const clerk = useClerk();
  const { session, isLoaded: isSessionLoaded } = useSession();
  const { user, isLoaded: isUserLoaded } = useUser();
  const { orgRole, orgSlug } = useAuth();

  // Set up application-wide logging
  const isDevMode = process.env.NODE_ENV === 'development' || inDebugMode();
  useLoggingSetup({
    enabled: isDevMode,
    logLevel: isDevMode ? LogLevel.DEBUG : LogLevel.INFO,
    browserConsoleCommands: isDevMode,
    timestampFormat: TimestampFormat.RELATIVE,
    // Enable specific channels (these are just examples)
    enabledChannels: isDevMode
      ? ['video-editor:commands', 'video-editor:batching']
      : [],
  });

  console.debug('Clerk Provider State:', {
    isSessionLoaded,
    isUserLoaded,
    sessionStatus: session?.status,
    hasUser: !!user,
    clerkLoaded: clerk.loaded,
  });

  // External services
  const ldClient = useLDClient();

  // URL Parameters
  const urlSearchParams = new URLSearchParams(window.location.search);
  const params = Object.fromEntries(urlSearchParams.entries());
  const { storyId } = params;
  // The Dato environment comes from the build, not the URL. Falling back here
  // keeps the store initialized even when the URL has no `env` yet — the
  // default-org redirect appends `?env=...` via client-side navigation, after
  // EditorEntry (which sits above the router) has already read the URL once.
  const env = params.env || process.env.REACT_APP_DATO_ENV;

  // Load Dato context
  const datoCtx = React.useMemo(() => DatoContextService.loadDatoContext(), []);

  // Consume the backend's SSO handoff ticket (if present) before the
  // needsRedirect gate below can bounce a not-yet-signed-in user away.
  const { isTicketPending } = useTicketSignIn();

  const { needsRedirect, hasValidAuth } = useAuthInitialization({
    datoCtx,
    clerkUser: user,
    clerkSession: session,
    isClerkLoaded: isSessionLoaded && isUserLoaded,
    ticketSignInPending: isTicketPending,
  });

  // Identify authenticated user in analytics providers (Datadog, PostHog, Segment)
  useAnalyticsIdentification({
    clerkUser: user,
    isClerkLoaded: isSessionLoaded && isUserLoaded,
  });

  // Initialize stores
  const { isReady } = useStoreInitialization({
    storyId,
    env,
    datoCtx,
    clerkUser: user,
    clerkSession: session,
    isClerkLoaded: isSessionLoaded && isUserLoaded,
    hasValidAuth,
  });

  // Initialize LaunchDarkly
  useLaunchDarklyInit({
    ldClient,
    storyId,
    clerkUser: user,
    isClerkLoaded: isSessionLoaded,
    orgRole,
    orgSlug,
  });
  // Faster TTFE kill switch — mirrors the LD flag into the readiness predicates.
  useAudioThenVideoFlagSync();
  // Audio Sidecar switch (PLAT-11697) — read at file-select in the Add Story flow.
  useAudioSidecarFlagSync();
  usePylonChatBubble();

  // Router configuration
  const videoCreatorElement = (
    <RootLayout>
      <CreatorStudioLanguageGate>
        <VideoCreator />
      </CreatorStudioLanguageGate>
    </RootLayout>
  );

  // Every route is a child of `ReviewerRouteGuard`, a pathless layout route:
  // translation-reviewer mode is defined once, by the allowlist in that guard,
  // instead of by which routes remembered to wrap themselves in it.
  const router = createBrowserRouter([
    {
      element: <ReviewerRouteGuard />,
      children: [
        {
          path: '/',
          element: (
            <RootLayout>
              <DefaultLandingRoute />
            </RootLayout>
          ),
        },
        {
          path: '/home',
          element: (
            <RootLayout>
              <HomePage />
            </RootLayout>
          ),
        },
        {
          path: '/creator',
          element: videoCreatorElement,
        },
        {
          path: '/content',
          element: (
            <RootLayout>
              <ContentStudio params={params} />
            </RootLayout>
          ),
        },
        {
          path: '/story-manager',
          element: (
            <RootLayout>
              <ErrorBoundary>
                <Dashboard params={params} />
              </ErrorBoundary>
            </RootLayout>
          ),
        },
        {
          path: '/speakers',
          element: (
            <RootLayout>
              <ErrorBoundary>
                <SpeakerManager />
              </ErrorBoundary>
            </RootLayout>
          ),
        },
        {
          path: '/broll',
          element: <BRollUpload />,
        },
        {
          path: '/brand-kit',
          element: (
            <RootLayout>
              <BrandKit />
            </RootLayout>
          ),
        },
        {
          path: '/settings',
          element: (
            <RootLayout>
              <Settings />
            </RootLayout>
          ),
        },
      ],
    },
  ]);

  // Dedicated studio sign-in page. Rendered before the auth-redirect and
  // store-init gates below so an unauthenticated user can actually reach it —
  // those gates would otherwise redirect to /sign-in or wait on auth forever
  // (useStoreInitialization keeps isReady=false without a valid session).
  if (window.location.pathname === '/sign-in') {
    // Honor the `redirect_url` the auth gate appends (return the user where they
    // were headed); fall back to /home only when it's absent.
    const afterSignInUrl = urlSearchParams.get('redirect_url') || '/home';
    return (
      <SignInScreen theme="dark">
        <SignIn routing="hash" afterSignInUrl={afterSignInUrl} />
      </SignInScreen>
    );
  }

  // A ticket sign-in is in flight — the session is about to hydrate, so
  // neither redirect to /sign-in nor let RedirectToSignIn fire yet.
  if (isTicketPending) {
    return <FullScreenLoadingOverlay text="Signing you in…" />;
  }

  // `!session` guards the ticket-completion frame: needsRedirect comes from
  // effect state and can be one commit stale, and RedirectToSignIn's own
  // effect (child) fires before useAuthInitialization's (parent) can clear
  // it. Mounting RedirectToSignIn with a live session makes Clerk bounce the
  // user to its configured Home URL — off the studio entirely.
  if (clerk.loaded && needsRedirect && !session) {
    return (
      <>
        <RedirectToSignIn />
      </>
    );
  }

  if (!isReady) {
    return <FullScreenLoadingOverlay text="Loading…" />;
  }

  const isSupportedBrowser =
    isChrome || isEdge || isChromium || isEdgeChromium || isMobileSafari;
  const shouldShowBrowserWarning = !inDebugMode() && !isSupportedBrowser;

  return (
    <ShortcutProvider>
      <SidebarProvider>
        <UpdateAvailabilityProvider>
          <RouterProvider router={router} />
          {shouldShowBrowserWarning && <UnsupportedBrowserModal />}
        </UpdateAvailabilityProvider>
      </SidebarProvider>
    </ShortcutProvider>
  );
});

const SignInScreen = styled.div`
  width: 100%;
  min-height: 100vh;
  background-color: var(--background);
  display: flex;
  justify-content: center;
  align-items: center;
`;

// Provider composition
export default EditorEntry;
